import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { WeatherDay } from "@weather/domain/models/weather-day.model";
import { WeatherHour } from "@weather/domain/models/weather-hour.model";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const WeatherSchema = z
    .object({
        stationId: z.int().positive(),
        stationName: z.string().trim(),
        stationTz: z.string().trim().nullable(),
        days: z.array(
            z.custom<WeatherDay>((val) => val instanceof WeatherDay, {
                error: "Invalid WeatherDay instance",
            }),
        ),
        hours: z.array(
            z.custom<WeatherHour>((val) => val instanceof WeatherHour, {
                error: "Invalid WeatherHour instance",
            }),
        ),
    })
    .brand<"Weather">();

export type WeatherInputProps = z.input<typeof WeatherSchema>;
type WeatherProps = z.output<typeof WeatherSchema>;

export class Weather {
    private constructor(private readonly props: Readonly<WeatherProps>) {}

    get stationId(): number {
        return this.props.stationId;
    }

    get stationName(): string {
        return this.props.stationName;
    }

    get stationTz(): string | null {
        return this.props.stationTz;
    }

    get days(): Readonly<WeatherDay>[] {
        return this.props.days;
    }

    get hours(): Readonly<WeatherHour>[] {
        return this.props.hours;
    }

    static validate(
        props: Readonly<MaybeValidProps<WeatherInputProps>>,
    ): Result<WeatherProps, z.ZodError<WeatherProps>> {
        const result = WeatherSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<WeatherInputProps>>,
    ): Result<WeatherProps, PartialValid<typeof Weather>> {
        const result = partialSafeParse(WeatherSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<WeatherInputProps>,
    ): Result<Weather, z.ZodError<WeatherProps>> {
        return Weather.validate(props).map(
            (validatedProps) => new Weather(validatedProps),
        );
    }

    static createWithRecovery(
        props: WeatherInputProps,
    ): Result<Weather, ModelError<typeof Weather>> {
        return Weather.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.stationId)
                    return new NonRecuperableModelError();

                const defaultProps: Unbrand<WeatherProps> = {
                    stationId: 0,
                    stationName: "",
                    stationTz: null,
                    days: [],
                    hours: [],
                };

                const recoveryProps: Unbrand<WeatherProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof Weather>(
                    new Weather(recoveryProps as WeatherProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new Weather(validatedProps));
    }

    toCreateProps(): WeatherInputProps {
        return {
            stationId: this.props.stationId,
            stationName: this.props.stationName,
            stationTz: this.props.stationTz,
            days: this.props.days,
            hours: this.props.hours,
        };
    }
}
